import type { QueryClient } from '@tanstack/react-query';

import { DISCOVER_TRACK_KEY } from '@/constants/queryKeys';
import { pickCaption } from '@/utils/commentDisplay';
import type { DiscoverComment, RecommendedTracksResponse } from '@/types';

/**
 * caption-edit-retranslate — la légende modifiée, écrite dans le cache du feed.
 *
 * ÉCRITURE DIRECTE, jamais `invalidateQueries` : le feed est un
 * `useInfiniteQuery`, l'invalider refetcherait toutes les pages chargées et
 * rebattrait le deck sous le doigt du lecteur (cf. `commentCountCache.ts`).
 *
 * SANS SCOPE DE CERCLE, à l'inverse de `bumpCommentCount` : une réponse
 * appartient à un cercle, la légende est la même dans tous les cercles où la
 * reco est partagée. Ne patcher qu'une entrée laisserait l'ancien texte en
 * changeant d'onglet.
 *
 * On écrit ce que le SERVEUR a renvoyé (texte trimé, `translations` à null),
 * jamais le texte saisi ; une réponse sans texte ne touche rien.
 */
export function patchCaptionInFeeds(
  queryClient: QueryClient,
  recommendationId: number,
  legend: Partial<DiscoverComment>,
): void {
  const content = legend?.content;
  if (typeof content !== 'string') return;
  const translations = legend.translations ?? null;

  queryClient.setQueriesData<{ pages: RecommendedTracksResponse[]; pageParams: unknown[] }>(
    { queryKey: DISCOVER_TRACK_KEY },
    (cached) => {
      if (!cached?.pages) return cached;

      let touched = false;
      const pages = cached.pages.map((page) => {
        if (!page?.data?.some((reco) => reco.id === recommendationId)) return page;
        return {
          ...page,
          data: page.data.map((reco) => {
            if (reco.id !== recommendationId) return reco;
            const caption = pickCaption(reco.comments);
            if (!caption) return reco;
            touched = true;
            return {
              ...reco,
              comments: (reco.comments ?? []).map((c) =>
                c === caption ? { ...c, content, translations } : c,
              ),
            };
          }),
        };
      });

      return touched ? { ...cached, pages } : cached;
    },
  );
}
